import type { Camera, GameSnapshot, ScreenPoint, SnapshotEntity } from "./types";

const BASE_TARGET_ZOOM = 1.45;
const MIN_TARGET_ZOOM = 0.72;
const REFERENCE_VIEWPORT_WIDTH = 1920;
const REFERENCE_VIEWPORT_HEIGHT = 1080;
const COMPACT_SHORTEST_SIDE_LIMIT = 700;
const MAX_VISIBLE_WORLD_RADIUS =
  Math.hypot(REFERENCE_VIEWPORT_WIDTH, REFERENCE_VIEWPORT_HEIGHT) * 0.5 / BASE_TARGET_ZOOM;

type CameraViewport = {
  width: number;
  height: number;
};

export function updateCamera(
  camera: Camera,
  snapshot: GameSnapshot,
  viewport?: CameraViewport
): void {
  const local = findLocalEntity(snapshot);

  camera.zoom = getTargetZoom(viewport?.width, viewport?.height);

  if (!local) {
    return;
  }

  camera.x = local.position.x;
  camera.y = local.position.y;
}

export function getTargetZoom(width = window.innerWidth, height = window.innerHeight): number {
  const safeWidth = Math.max(1, width);
  const safeHeight = Math.max(1, height);
  const responsiveZoom = getResponsiveZoom(safeWidth, safeHeight);

  if (!shouldLimitWorldVisibility(safeWidth, safeHeight)) {
    return responsiveZoom;
  }

  const requiredZoom =
    Math.hypot(safeWidth, safeHeight) * 0.5 / MAX_VISIBLE_WORLD_RADIUS;

  return Math.max(responsiveZoom, requiredZoom);
}

export function worldToScreen(
  x: number,
  y: number,
  camera: Camera,
  width: number,
  height: number
): ScreenPoint {
  return {
    x: width * 0.5 + (x - camera.x) * camera.zoom,
    y: height * 0.5 + (y - camera.y) * camera.zoom
  };
}

export function worldSizeToScreen(value: number, camera: Camera): number {
  return value * camera.zoom;
}

export function findLocalEntity(snapshot: GameSnapshot): SnapshotEntity | null {
  const localId = extractId(snapshot.local_player_id ?? snapshot.localPlayerId);

  if (localId === null) {
    return (
      snapshot.entities.find((entity) => {
        const kind = normalizeKind(entity.kind);
        return kind === "player" || kind === "bot";
      }) ?? null
    );
  }

  return (
    snapshot.entities.find((entity) => {
      const kind = normalizeKind(entity.kind);
      const owner = extractId(entity.owner);

      return (kind === "player" || kind === "bot") && owner === localId;
    }) ?? null
  );
}

export function extractId(value: unknown): number | string | null {
  if (typeof value === "number" || typeof value === "string") {
    return value;
  }

  if (value && typeof value === "object" && "0" in value) {
    const nested = (value as { 0?: unknown })[0];

    if (typeof nested === "number" || typeof nested === "string") {
      return nested;
    }
  }

  return null;
}

export function normalizeKind(kind: string): string {
  return String(kind).toLowerCase();
}

function getResponsiveZoom(width: number, height: number): number {
  const shortestSide = Math.min(width, height);
  const isLandscape = width > height;
  const viewportScale = clamp(shortestSide / 720, 0.52, 1);
  const heightScale = isLandscape ? clamp(height / 560, 0.58, 1) : 1;
  const widthScale = isLandscape ? 1 : clamp(width / 520, 0.58, 1);
  const scale = Math.min(viewportScale, heightScale, widthScale);

  return clamp(BASE_TARGET_ZOOM * scale, MIN_TARGET_ZOOM, BASE_TARGET_ZOOM);
}

function shouldLimitWorldVisibility(width: number, height: number): boolean {
  const shortestSide = Math.min(width, height);

  if (shortestSide <= COMPACT_SHORTEST_SIDE_LIMIT) {
    return false;
  }

  return width > REFERENCE_VIEWPORT_WIDTH || height > REFERENCE_VIEWPORT_HEIGHT;
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}